import { useState, useEffect, useCallback } from 'react';
import { rmmAPI, usersAPI, sitesAPI } from '@/lib/api';
import { BACKEND_URL } from '@/lib/backend';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, KeyRound, Plus, Ban, Copy, Download, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';
import { SigningCertCard } from './SigningCertCard';

export const EnrollmentTab = ({ isAdmin }) => {
  const [keys, setKeys] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ customer_id: '', site_id: '', label: '', heartbeat_interval: 60, expires_hours: 'never', single_use: false });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    rmmAPI.listKeys().then((res) => setKeys(res.data)).catch(() => setKeys([]));
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) {
      usersAPI.list('customer').then((res) => setCustomers(res.data || [])).catch(() => {});
    }
  }, [load, isAdmin]);

  useEffect(() => {
    if (!form.customer_id) { setSites([]); return; }
    sitesAPI.list({ customer_id: form.customer_id }).then((res) => setSites(res.data || [])).catch(() => setSites([]));
  }, [form.customer_id]);

  const create = async () => {
    if (!form.customer_id) { toast.error('Select a customer'); return; }
    setCreating(true);
    try {
      await rmmAPI.createKey({
        customer_id: form.customer_id,
        site_id: form.site_id || null,
        label: form.label || null,
        heartbeat_interval: parseInt(form.heartbeat_interval) || 60,
        expires_hours: form.expires_hours === 'never' ? null : parseInt(form.expires_hours) || null,
        single_use: form.single_use,
      });
      toast.success('Enrollment key created');
      setForm({ ...form, label: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this enrollment key? New devices will no longer be able to register with it.')) return;
    try {
      await rmmAPI.revokeKey(id);
      toast.success('Key revoked');
      load();
    } catch { toast.error('Failed to revoke key'); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(() => toast.success('Key copied to clipboard'));
  };

  // Build the config-carrying filename locally so the download name NEVER
  // depends on the server's Content-Disposition header (which browsers block
  // cross-origin — that silently dropped the config and broke installs). The
  // Go installer reads its server URL + enrollment key from this file name.
  const cfgInstallerName = (key) => {
    try {
      const token = btoa(`${BACKEND_URL}|${key}`)
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      return `MVP-IT-RMM-Installer__CFG-${token}.exe`;
    } catch {
      return 'MVP-IT-RMM-Installer.exe';
    }
  };

  const downloadExe = async (id, key) => {
    try {
      const res = await rmmAPI.agentExe(id);
      // Prefer the locally-computed config filename; fall back to the server's
      // Content-Disposition only if we somehow don't have the raw key.
      let fname = key ? cfgInstallerName(key) : null;
      if (!fname) {
        const cd = res.headers['content-disposition'] || '';
        const m = cd.match(/filename="?([^"]+)"?/);
        fname = m ? m[1] : 'MVP-IT-RMM-Installer.exe';
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Signed .EXE installer downloaded — keep the file name, then double-click it on the Windows device');
    } catch { toast.error('Failed to download installer'); }
  };

  const downloadCert = async () => {
    try {
      const res = await rmmAPI.agentSigningCert();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'MVP-IT-CodeSigning.cer';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Code-signing certificate downloaded — deploy it to Trusted Publishers via GPO/Intune');
    } catch { toast.error('Failed to download certificate'); }
  };

  const downloadBlob = async (fn, filename, okMsg) => {
    try {
      const res = await fn();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(okMsg);
    } catch { toast.error(`Failed to download ${filename}`); }
  };

  const downloadScript = async (id) => {
    try {
      const res = await rmmAPI.agentScript(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'mvpit-rmm-agent.ps1';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Agent script downloaded — run it as Administrator on the Windows device');
    } catch { toast.error('Failed to download script'); }
  };

  return (
    <div className="space-y-6" data-testid="rmm-enrollment-tab">
      {isAdmin && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-500" /> Create Enrollment Key</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-700">Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, site_id: '' })}>
                  <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="rmm-key-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Site (optional)</Label>
                <Select value={form.site_id || 'none'} onValueChange={(v) => setForm({ ...form, site_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="rmm-key-site-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific site</SelectItem>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Label</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main office rollout" className="bg-white border-slate-200 mt-1" data-testid="rmm-key-label-input" />
              </div>
              <div>
                <Label className="text-slate-700">Heartbeat (seconds)</Label>
                <Input type="number" min="15" max="3600" value={form.heartbeat_interval} onChange={(e) => setForm({ ...form, heartbeat_interval: e.target.value })} className="bg-white border-slate-200 mt-1" data-testid="rmm-key-interval-input" />
              </div>
              <div>
                <Label className="text-slate-700">Expires In</Label>
                <Select value={String(form.expires_hours)} onValueChange={(v) => setForm({ ...form, expires_hours: v })}>
                  <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="rmm-key-expiry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="72">72 hours</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                    <SelectItem value="720">30 days</SelectItem>
                    <SelectItem value="never">Never (default - one installer per client)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Usage</Label>
                <Select value={form.single_use ? 'single' : 'multi'} onValueChange={(v) => setForm({ ...form, single_use: v === 'single' })}>
                  <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="rmm-key-usage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multi">All devices for this client (default)</SelectItem>
                    <SelectItem value="single">Single device only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={create} disabled={creating} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="rmm-create-key-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Create Key
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Enrollment Keys</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="rmm-keys-list">
          {!keys ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No enrollment keys yet.{isAdmin ? ' Create one above to enroll devices.' : ' Ask an admin to create one.'}</p>
          ) : keys.map((k) => (
            <div key={k.id} className="p-3 rounded-xl border border-slate-200 space-y-2" data-testid={`rmm-key-${k.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800">{k.label || 'Enrollment key'}</p>
                    {k.active ? <Badge className="bg-emerald-100 text-emerald-700">active</Badge>
                      : k.used_at ? <Badge className="bg-slate-200 text-slate-600">used</Badge>
                      : k.expired ? <Badge className="bg-amber-100 text-amber-700">expired</Badge>
                      : <Badge className="bg-slate-200 text-slate-600">revoked</Badge>}
                    {k.single_use && <Badge variant="outline" className="text-[10px]">single-use</Badge>}
                    <Badge variant="secondary">{k.devices_enrolled || 0} device{(k.devices_enrolled || 0) === 1 ? '' : 's'}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    {k.customer_name}{k.site_name ? ` · ${k.site_name}` : ' · all sites'} · heartbeat {k.heartbeat_interval}s
                    {k.expires_at ? ` · expires ${fmtWhen(k.expires_at)}` : ' · never expires'} · by {k.created_by} · {fmtWhen(k.created_at)}
                  </p>
                  {k.active && <p className="text-xs font-mono text-slate-400 truncate mt-1">{k.key}</p>}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {k.active && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyKey(k.key)} data-testid={`rmm-copy-key-${k.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                      <Button size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => downloadExe(k.id, k.key)} data-testid={`rmm-download-exe-${k.id}`}>
                        <Download className="w-4 h-4 mr-1" /> .EXE Installer
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadScript(k.id)} data-testid={`rmm-download-script-${k.id}`}>
                        <Download className="w-4 h-4 mr-1" /> PS1 Script
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="rounded-full text-red-500 hover:text-red-600" onClick={() => revoke(k.id)} data-testid={`rmm-revoke-key-${k.id}`}>
                          <Ban className="w-4 h-4 mr-1" /> Revoke
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg flex items-center gap-2"><Terminal className="w-5 h-5 text-slate-500" /> How to enroll a Windows device</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-1.5">
          <p><b>Easiest — the signed .EXE installer:</b></p>
          <p>1. Create an enrollment key for the customer (and optional site).</p>
          <p>2. Click <b>.EXE Installer</b> to download the code-signed <code className="bg-slate-100 px-1 rounded">MVP-IT-RMM-Installer…exe</code>. It's signed by <b>MVP IT</b> and carries the key + server URL in its file name — <b>don't rename it</b>.</p>
          <p>3. On the customer's Windows PC, <b>double-click it and approve the UAC prompt</b>. It downloads the latest agent, installs the <b>"MVP IT RMM Agent"</b> background task, trusts the MVP IT certificate, adds the taskbar tray icon and a Control Panel entry. The device appears here within ~1 minute. Use the <b>same file on every PC of that client</b>.</p>
          <p>4. <b>Silent / mass deployment (GPO or Intune):</b> run it with <code className="bg-slate-100 px-1 rounded">MVP-IT-RMM-Installer…exe /silent</code> from a SYSTEM/admin context — no prompts, no window.</p>
          <div className="p-2.5 rounded-lg border border-cyan-200 bg-cyan-50/50 space-y-1.5 mt-1">
            <p className="text-slate-700"><b>Remove the SmartScreen "unknown publisher" warning on your fleet (one-time):</b></p>
            <p>Download the <b>Signing Certificate</b> below and deploy it to your managed PCs via Group Policy → <i>Computer Configuration ▸ Windows Settings ▸ Security Settings ▸ Public Key Policies</i>, importing it into <b>Trusted Root Certification Authorities</b> and <b>Trusted Publishers</b> (or the equivalent Intune trusted-certificate profiles). Once trusted, the MVP IT installer runs with no warning.</p>
            <Button size="sm" variant="outline" className="rounded-full mt-1" onClick={downloadCert} data-testid="rmm-download-cert">
              <Download className="w-4 h-4 mr-1" /> Signing Certificate (.cer)
            </Button>
          </div>
          <p className="pt-2"><b>Advanced — the PowerShell script</b> (for scripted rollouts):</p>
          <p>Download <b>PS1 Script</b>, then run <code className="bg-slate-100 px-1 rounded">.\mvpit-rmm-agent.ps1 -Install</code> in an elevated PowerShell. Use <code className="bg-slate-100 px-1 rounded">-Uninstall</code> to remove it. No inbound firewall ports are ever required.</p>
          <div className="p-2.5 rounded-lg border border-slate-200 bg-slate-50 space-y-1.5 mt-2">
            <p className="text-slate-700"><b>Agent tooling (signed by MVP IT):</b></p>
            <p className="text-xs text-slate-500">The <b>Uninstaller</b> cleanly removes the agent (stops the task, deletes credentials) — run it interactively or with <code className="bg-slate-100 px-1 rounded">/silent</code> under GPO/Intune. The <b>Capture Helper</b> is the native 30+ FPS screen/input engine used for Remote Control; the agent fetches it automatically on first use, but you can pre-stage it here.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadBlob(rmmAPI.agentUninstallerExe, 'MVP-IT-RMM-Uninstaller.exe', 'Signed uninstaller downloaded — double-click on the device (or run /silent under GPO)')} data-testid="rmm-download-uninstaller">
                <Download className="w-4 h-4 mr-1" /> Uninstaller (.exe)
              </Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadBlob(rmmAPI.agentCaptureHelperExe, 'mvpit-capture.exe', 'Signed capture helper downloaded')} data-testid="rmm-download-capture-helper">
                <Download className="w-4 h-4 mr-1" /> Capture Helper (.exe)
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-400 pt-1">One installer works for every PC of that client (keys are multi-device and never expire by default; revoke a key any time). Re-running it on the same PC re-uses the existing device record. Every device gets its own credential, revocable from the device panel. The agent shows a <b>tray icon</b> (green = active), appears in <b>Control Panel &rsaquo; Programs and Features</b> for local removal, and can be uninstalled remotely from the device panel.</p>
        </CardContent>
      </Card>

      {isAdmin && <SigningCertCard />}
    </div>
  );
};
